import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import './OutstandingCallPage.css';
import './SoutheasternNewEntry.css';

const EntryDataPage = () => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(4);
  const [filterText, setFilterText] = useState('');
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchText, setSearchText] = useState('');

  // Filter dropdown states
  const [filterTypes, setFilterTypes] = useState([]);
  const [isFilterDropdownOpen, setIsFilterDropdownOpen] = useState(false);
  const [filterSearchText, setFilterSearchText] = useState('');
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [scheduleForm, setScheduleForm] = useState({
    scheduleDate: '',
    time: '',
    assignmentTime: '',
    teamSelection: ''
  });
  const [scheduleSubmitting, setScheduleSubmitting] = useState(false);
  const [teams, setTeams] = useState([]);
  const [isAssignmentTimeOpen, setIsAssignmentTimeOpen] = useState(false);
  const [isScheduleDateDisabled, setIsScheduleDateDisabled] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [editForm, setEditForm] = useState({
    dateReceived: "",
    dateScheduled: "",
    priorities: "",
    taylorTechnologyNumber: "",
    clientReferenceNo: "",
    locations: "",
    siteDetails: "",
    quotationNo: "",
    value: "",
    jobNo: "",
    station: "",
    descriptionOfWorks: "",
    returnVisits: false,
    cherryPickerRequired: false,
    returnSiteDetails: "",
    returnAdditionalNotes: ""
  });
  const [editFieldErrors, setEditFieldErrors] = useState({});
  const [editSubmitting, setEditSubmitting] = useState(false);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isDropdownOpen && !event.target.closest('.multi-select-dropdown')) {
        setIsDropdownOpen(false);
      }
      if (isAssignmentTimeOpen && !event.target.closest('.multi-select-dropdown')) {
        setIsAssignmentTimeOpen(false);
      }
      if (isFilterDropdownOpen && !event.target.closest('.multi-select-dropdown')) {
        setIsFilterDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen, isAssignmentTimeOpen, isFilterDropdownOpen]);

  // Fetch data from Southeastern API
  useEffect(() => {
    const fetchSoutheasternData = async () => {
      try {
        const apiUrl = process.env.REACT_APP_API_URL;
        const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
        
        const token = localStorage.getItem("token");
        if (!token) {
          toast.error("Please login again");
          return;
        }
        
        const config = { headers: { Authorization: `Bearer ${token}` } };
        const response = await axios.get(`${baseUrl}/api/southeastern`, config);
        
        if (response.data.success) {
          setData(response.data.data);
        } else {
          toast.error("Failed to fetch data");
        }
      } catch (error) {
        console.error("Error fetching Entry Data:", error);
        toast.error("Failed to fetch data");
      } finally {
        setLoading(false);
      }
    };

    fetchSoutheasternData();
  }, []);

  // Fetch teams data (filtered for Southeastern)
  useEffect(() => {
    const fetchTeamsData = async () => {
      try {
        const apiUrl = process.env.REACT_APP_API_URL;
        const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
        
        const token = localStorage.getItem("token");
        if (!token) {
          return;
        }
        
        const config = { headers: { Authorization: `Bearer ${token}` } };
        const response = await axios.get(`${baseUrl}/api/team`, config);
        
        if (response.data.success) {
          // Filter teams where assignTo === 'southeastern'
          const southeasternTeams = response.data.data.filter(team => 
            team.assignTo && team.assignTo.toLowerCase() === 'southeastern'
          );
          setTeams(southeasternTeams);
        }
      } catch (error) {
        console.error("Error fetching teams data:", error);
      }
    };

    fetchTeamsData();
  }, []);

  // Filter data based on search text and filter types
  const filteredData = data.filter(item => {
    // Text search filter
    if (filterText) {
      const searchText = filterText.toLowerCase();
      const matchesSearch = (
        (item.dateReceived && item.dateReceived.toLowerCase().includes(searchText)) ||
        (item.dateScheduled && item.dateScheduled.toLowerCase().includes(searchText)) ||
        (item.priorities && item.priorities.toLowerCase().includes(searchText)) ||
        (item.taylorTechnologyNumber && item.taylorTechnologyNumber.toLowerCase().includes(searchText)) ||
        (item.clientReferenceNo && item.clientReferenceNo.toLowerCase().includes(searchText)) ||
        (item.locations && item.locations.toLowerCase().includes(searchText)) ||
        (item.siteDetails && item.siteDetails.toLowerCase().includes(searchText)) ||
        (item.quotationNo && item.quotationNo.toLowerCase().includes(searchText)) ||
        (item.value && item.value.toLowerCase().includes(searchText)) ||
        (item.jobNo && item.jobNo.toLowerCase().includes(searchText)) ||
        (item.station && item.station.toLowerCase().includes(searchText)) ||
        (item.descriptionOfWorks && item.descriptionOfWorks.toLowerCase().includes(searchText)) ||
        (item.returnSiteDetails && item.returnSiteDetails.toLowerCase().includes(searchText)) ||
        (item.returnAdditionalNotes && item.returnAdditionalNotes.toLowerCase().includes(searchText))
      );
      if (!matchesSearch) return false;
    }

    // Type filter
    if (filterTypes.length > 0) {
      if (!item.type) return false; // If no type assigned, don't show when filtering

      const itemTypes = item.type.split(',').map(t => parseInt(t.trim()));
      const hasMatchingType = filterTypes.some(filterType =>
        itemTypes.includes(filterType)
      );
      if (!hasMatchingType) return false;
    }

    return true;
  });

  // Sort filtered data by updatedAt (latest modified first), then by createdAt
  const sortedData = filteredData.sort((a, b) => {
    // First sort by updatedAt (if available)
    if (a.updatedAt && b.updatedAt) {
      const dateA = new Date(a.updatedAt);
      const dateB = new Date(b.updatedAt);
      if (dateA.getTime() !== dateB.getTime()) {
        return dateB.getTime() - dateA.getTime(); // Descending order (latest first)
      }
    }
    
    // If updatedAt is same or not available, sort by createdAt
    if (a.createdAt && b.createdAt) {
      const dateA = new Date(a.createdAt);
      const dateB = new Date(b.createdAt);
      return dateB.getTime() - dateA.getTime(); // Descending order (latest first)
    }
    
    // If no dates available, maintain original order
    return 0;
  });

  // Pagination
  const totalPages = Math.ceil(sortedData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentData = sortedData.slice(startIndex, endIndex);

  const handleSchedule = (item) => {
    setSelectedItem(item);
    
    // Pre-populate scheduleDate if available
    let prefillDate = '';
    let hasExistingDate = false;
    
    // First check if scheduleTask exists (already scheduled)
    if (item.scheduleTask) {
      try {
        const scheduleData = JSON.parse(item.scheduleTask);
        prefillDate = scheduleData.scheduleDate || '';
        if (prefillDate) {
          hasExistingDate = true;
        }
      } catch (e) {
        // If parsing fails, try to use dateScheduled
        if (item.dateScheduled) {
          prefillDate = item.dateScheduled;
          hasExistingDate = true;
        }
      }
    } 
    // If no scheduleTask, check dateScheduled
    else if (item.dateScheduled) {
      prefillDate = item.dateScheduled;
      hasExistingDate = true;
    }
    
    // Format date for input (YYYY-MM-DD)
    if (prefillDate) {
      // If date is in different format, convert it
      const dateObj = new Date(prefillDate);
      if (!isNaN(dateObj.getTime())) {
        const year = dateObj.getFullYear();
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const day = String(dateObj.getDate()).padStart(2, '0');
        prefillDate = `${year}-${month}-${day}`;
      }
    }
    
    // Disable date field if date already exists
    setIsScheduleDateDisabled(hasExistingDate);
    
    setScheduleForm({
      scheduleDate: prefillDate,
      time: '',
      assignmentTime: '',
      teamSelection: ''
    });
    
    setShowScheduleModal(true);
  };

  const handleScheduleInputChange = (e) => {
    const { name, value } = e.target;
    setScheduleForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAssignmentTimeSelect = (hour) => {
    setScheduleForm(prev => ({
      ...prev,
      assignmentTime: hour
    }));
    setIsAssignmentTimeOpen(false);
  };

  const handleScheduleSubmit = async (e) => {
    e.preventDefault();
    
    if (!scheduleForm.scheduleDate || !scheduleForm.time || !scheduleForm.assignmentTime || !scheduleForm.teamSelection) {
      toast.error('Fill all fields');
      return;
    }

    setScheduleSubmitting(true);
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
      
      const token = localStorage.getItem("token");
      if (!token) {
        toast.error("Please login again");
        return;
      }
      
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      // Create schedule data object
      const scheduleData = {
        scheduleDate: scheduleForm.scheduleDate,
        time: scheduleForm.time,
        assignmentTime: scheduleForm.assignmentTime,
        teamSelection: scheduleForm.teamSelection,
        createdAt: new Date().toISOString()
      };
      
      // Update the specific Southeastern record with schedule data
      const updateResponse = await axios.put(`${baseUrl}/api/southeastern/${selectedItem._id}`, 
        { scheduleTask: JSON.stringify(scheduleData) }, 
        config
      );
      
      if (updateResponse.data.success) {
        toast.success('Schedule created');
        setShowScheduleModal(false);
        setScheduleForm({
          scheduleDate: '',
          time: '',
          assignmentTime: '',
          teamSelection: ''
        });
        setSelectedItem(null);
        
        // Redirect to calendar page with Schedule Date
        const scheduleDate = new Date(scheduleForm.scheduleDate);
        const year = scheduleDate.getFullYear();
        const month = String(scheduleDate.getMonth() + 1).padStart(2, '0');
        const day = String(scheduleDate.getDate()).padStart(2, '0');
        navigate(`/admin/calendar/Southeastern?date=${year}-${month}-${day}`);
      } else {
        toast.error('Failed to save');
      }
      
    } catch (error) {
      console.error('Error creating schedule:', error);
      toast.error('Failed to create');
    } finally {
      setScheduleSubmitting(false);
    }
  };

  const handleCloseScheduleModal = () => {
    setShowScheduleModal(false);
    setScheduleForm({
      scheduleDate: '',
      time: '',
      assignmentTime: '',
      teamSelection: ''
    });
    setSelectedItem(null);
    setIsScheduleDateDisabled(false);
  };

  // Handle edit
  const handleEdit = (item) => {
    setEditItem(item);
    // Extract Taylor Technology Number without TTL prefix for display
    let ttlNumberDisplay = item.taylorTechnologyNumber || '';
    if (ttlNumberDisplay.toUpperCase().startsWith('TTL')) {
      ttlNumberDisplay = ttlNumberDisplay.substring(3);
    }
    
    setEditForm({
      dateReceived: item.dateReceived || "",
      dateScheduled: item.dateScheduled || "",
      priorities: item.priorities || "",
      taylorTechnologyNumber: ttlNumberDisplay,
      clientReferenceNo: item.clientReferenceNo || "",
      locations: item.locations || "",
      siteDetails: item.siteDetails || "",
      quotationNo: item.quotationNo || "",
      value: item.value || "",
      jobNo: item.jobNo || "",
      station: item.station || "",
      descriptionOfWorks: item.descriptionOfWorks || "",
      returnVisits: item.returnVisits || false,
      cherryPickerRequired: item.cherryPickerRequired || false,
      returnSiteDetails: item.returnSiteDetails || "",
      returnAdditionalNotes: item.returnAdditionalNotes || ""
    });
    setEditFieldErrors({});
    setShowEditModal(true);
  };

  const handleEditChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Clear field error when user starts typing
    if (editFieldErrors[name]) {
      setEditFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
    
    // If returnVisits checkbox is unchecked, clear return details fields
    if (name === 'returnVisits' && !checked) {
      setEditForm({ 
        ...editForm, 
        [name]: false,
        returnSiteDetails: "",
        returnAdditionalNotes: "",
        cherryPickerRequired: false
      });
    } else {
      // For Taylor Technology Number, only allow numbers
      if (name === 'taylorTechnologyNumber' && type !== 'checkbox') {
        const numericValue = value.replace(/[^0-9]/g, '');
        setEditForm({ 
          ...editForm, 
          [name]: numericValue 
        });
      } 
      // For Job No, only allow numbers
      else if (name === 'jobNo' && type !== 'checkbox') {
        const numericValue = value.replace(/[^0-9]/g, '');
        setEditForm({ 
          ...editForm, 
          [name]: numericValue 
        });
      } else {
        setEditForm({ 
          ...editForm, 
          [name]: type === 'checkbox' ? checked : value 
        });
      }
    }
  };

  const handleEditSave = async (e) => {
    e.preventDefault();
    
    // Clear previous field errors
    setEditFieldErrors({});
    
    setEditSubmitting(true);
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
      
      const token = localStorage.getItem("token");
      if (!token) {
        toast.error("Please login again");
        return;
      }
      
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      // Prepare form data with TTL prefix for taylorTechnologyNumber
      const formData = { ...editForm };
      if (formData.taylorTechnologyNumber && !formData.taylorTechnologyNumber.toUpperCase().startsWith('TTL')) {
        formData.taylorTechnologyNumber = `TTL${formData.taylorTechnologyNumber}`;
      }
      
      await axios.put(`${baseUrl}/api/southeastern/${editItem._id}`, formData, config);
      
      toast.success("Southeastern entry updated successfully");
      setShowEditModal(false);
      setEditItem(null);
      setEditForm({
        dateReceived: "",
        dateScheduled: "",
        priorities: "",
        taylorTechnologyNumber: "",
        clientReferenceNo: "",
        locations: "",
        siteDetails: "",
        quotationNo: "",
        value: "",
        jobNo: "",
        station: "",
        descriptionOfWorks: "",
        returnVisits: false,
        cherryPickerRequired: false,
        returnSiteDetails: "",
        returnAdditionalNotes: ""
      });
      
      // Refresh data
      const response = await axios.get(`${baseUrl}/api/southeastern`, config);
      if (response.data.success) {
        setData(response.data.data);
      }
    } catch (err) {
      console.error("Error:", err);
      
      // Handle validation errors from backend
      if (err.response && err.response.data) {
        // Handle field-specific errors
        if (err.response.data.field) {
          const { field, message } = err.response.data;
          setEditFieldErrors({ [field]: message });
          
          // Focus on the problematic field
          setTimeout(() => {
            const fieldElement = document.querySelector(`#editModal [name="${field}"]`);
            if (fieldElement) {
              fieldElement.focus();
              fieldElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }, 100);
          return;
        }
      }
      
      toast.error("Failed to update Southeastern entry");
    } finally {
      setEditSubmitting(false);
    }
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditItem(null);
    setEditForm({
      dateReceived: "",
      dateScheduled: "",
      priorities: "",
      taylorTechnologyNumber: "",
      clientReferenceNo: "",
      locations: "",
      siteDetails: "",
      quotationNo: "",
      value: "",
      jobNo: "",
      station: "",
      descriptionOfWorks: "",
      returnVisits: false,
      cherryPickerRequired: false,
      returnSiteDetails: "",
      returnAdditionalNotes: ""
    });
    setEditFieldErrors({});
  };

  // Checkbox handling functions
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allIds = new Set(currentData.map(item => item._id || item.id));
      setSelectedItems(allIds);
    } else {
      setSelectedItems(new Set());
    }
  };

  const handleSelectItem = (itemId) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(itemId)) {
      newSelected.delete(itemId);
    } else {
      newSelected.add(itemId);
    }
    setSelectedItems(newSelected);
  };

  const isAllSelected = currentData.length > 0 && currentData.every(item => selectedItems.has(item._id || item.id));
  const isIndeterminate = selectedItems.size > 0 && selectedItems.size < currentData.length;

  // Type mapping
  const typeOptions = [
    { label: 'Outstanding Call', value: 1 },
    { label: 'Outstanding Quotes', value: 2 },
    { label: 'Return Visit', value: 3 },
    { label: 'Outstanding PPMs', value: 4 }
  ];

  // Handle type selection
  const handleTypeSelect = (option) => {
    if (selectedTypes.includes(option.value)) {
      setSelectedTypes(selectedTypes.filter(type => type !== option.value));
    } else {
      setSelectedTypes([...selectedTypes, option.value]);
    }
    setSearchText('');
  };

  const handleRemoveType = (valueToRemove) => {
    setSelectedTypes(selectedTypes.filter(type => type !== valueToRemove));
  };

  const handleClearAll = () => {
    setSelectedTypes([]);
    setSearchText('');
  };

  // Filter options based on search text
  const filteredOptions = typeOptions.filter(option =>
    option.label.toLowerCase().includes(searchText.toLowerCase())
  );

  // Handle filter type selection
  const handleFilterTypeSelect = (option) => {
    if (filterTypes.includes(option.value)) {
      setFilterTypes(filterTypes.filter(type => type !== option.value));
    } else {
      setFilterTypes([...filterTypes, option.value]);
    }
    setFilterSearchText('');
  };

  const handleFilterRemoveType = (valueToRemove) => {
    setFilterTypes(filterTypes.filter(type => type !== valueToRemove));
  };

  const handleFilterClearAll = () => {
    setFilterTypes([]);
    setFilterSearchText('');
  };

  // Filter options for filter dropdown
  const filteredFilterOptions = typeOptions.filter(option =>
    option.label.toLowerCase().includes(filterSearchText.toLowerCase())
  );

  // Handle submit
  const handleSubmit = async () => {
    if (selectedItems.size === 0) {
      toast.error("Select at least one");
      return;
    }
    if (selectedTypes.length === 0) {
      toast.error("Select at least one");
      return;
    }

    setIsSubmitting(true);
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
      
      const token = localStorage.getItem("token");
      if (!token) {
        toast.error("Please login again");
        return;
      }

      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      // Prepare the type value - if multiple types selected, join with comma
      const typeValue = selectedTypes.length === 1 ? selectedTypes[0].toString() : selectedTypes.join(',');
      
      // Update each selected item
      const updatePromises = Array.from(selectedItems).map(async (itemId) => {
        return axios.put(`${baseUrl}/api/southeastern/${itemId}`, 
          { type: typeValue }, 
          config
        );
      });

      await Promise.all(updatePromises);
      
      toast.success(`Updated ${selectedItems.size} items`);
      
      // Clear selections and refresh data
      setSelectedItems(new Set());
      setSelectedTypes([]);
      
      // Refresh data
      const response = await axios.get(`${baseUrl}/api/southeastern`, config);
      if (response.data.success) {
        setData(response.data.data);
      }
      
    } catch (error) {
      console.error("Error updating items:", error);
      toast.error("Failed to update");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="outstanding-call-page no-max-height">
      <div className="table-container">
        {/* Control Bar */}
        <div className="control-bar">
          {/* <div className="control-left">
            <span>Show</span>
            <select 
              value={itemsPerPage} 
              onChange={(e) => setItemsPerPage(Number(e.target.value))}
              className="items-per-page"
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <span>result per page</span>
            {selectedItems.size > 0 && (
              <span style={{ marginLeft: '20px', color: '#8b1634', fontWeight: 'bold' }}>
                {selectedItems.size} item(s) selected
              </span>
            )}
          </div> */}
          
          <div className="control-right">
            <input
              type="text"
              placeholder="Filter in records..."
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              className="filter-input"
            />
            
            {/* Multi-select dropdown for types */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginLeft: '10px', position: 'relative' }}>
              <span style={{ fontSize: '14px', color: '#6c757d' }}>Assign Type:</span>
              <div className="multi-select-dropdown" style={{ position: 'relative', minWidth: '250px' }}>
                {/* Input field with tags */}
                <div 
                  style={{
                    border: '1px solid #007bff',
                    borderRadius: '4px',
                    padding: '8px',
                    minHeight: '38px',
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: '4px',
                    backgroundColor: 'white',
                    cursor: 'text'
                  }}
                  onClick={() => setIsDropdownOpen(true)}
                >
                  {/* Selected type tags */}
                  {selectedTypes.map((typeValue) => {
                    const option = typeOptions.find(opt => opt.value === typeValue);
                    return (
                      <span
                        key={typeValue}
                        style={{
                          backgroundColor: '#8b1634',
                          color: 'white',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}
                      >
                        {option?.label}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveType(typeValue);
                          }}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: 'white',
                            cursor: 'pointer',
                            padding: '0',
                            fontSize: '12px',
                            lineHeight: '1'
                          }}
                        >
                          ×
                        </button>
                      </span>
                    );
                  })}
                  
                  {/* Input field for typing */}
                  <input
                    type="text"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    onFocus={() => setIsDropdownOpen(true)}
                    placeholder={selectedTypes.length === 0 ? "Select types..." : ""}
                    style={{
                      border: 'none',
                      outline: 'none',
                      flex: 1,
                      minWidth: '100px',
                      fontSize: '14px'
                    }}
                  />
                  
                  {/* Clear all button */}
                  {selectedTypes.length > 0 && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleClearAll();
                      }}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#6c757d',
                        cursor: 'pointer',
                        padding: '0',
                        fontSize: '16px',
                        lineHeight: '1'
                      }}
                    >
                      ×
                    </button>
                  )}
                </div>

                {/* Dropdown list */}
                {isDropdownOpen && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      backgroundColor: 'white',
                      border: '1px solid #dee2e6',
                      borderRadius: '4px',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      zIndex: 1000,
                      maxHeight: '200px',
                      overflowY: 'auto'
                    }}
                  >
                    {filteredOptions.length > 0 ? (
                      filteredOptions.map((option) => (
                        <div
                          key={option.value}
                          onClick={() => handleTypeSelect(option)}
                          style={{
                            padding: '8px 12px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            backgroundColor: selectedTypes.includes(option.value) ? '#FCE4EC' : 'white',
                            borderBottom: '1px solid #f1f3f4'
                          }}
                          onMouseEnter={(e) => {
                            if (!selectedTypes.includes(option.value)) {
                              e.target.style.backgroundColor = '#f8f9fa';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!selectedTypes.includes(option.value)) {
                              e.target.style.backgroundColor = 'white';
                            }
                          }}
                        >
                          {option.label}
                        </div>
                      ))
                    ) : (
                      <div style={{ padding: '8px 12px', color: '#6c757d', fontSize: '14px' }}>
                        No options found
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Submit button */}
            <button
              className="new-entry-btn"
              onClick={handleSubmit}
              disabled={isSubmitting}
              style={{
                marginLeft: '10px',
                opacity: isSubmitting ? 0.6 : 1,
                cursor: isSubmitting ? 'not-allowed' : 'pointer'
              }}
            >
              {isSubmitting ? 'Submitting...' : 'Submit'}
            </button>

            {/* Filter dropdown */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginLeft: '10px', position: 'relative' }}>
              <span style={{ fontSize: '14px', color: '#6c757d' }}>Filter by Type:</span>
              <div className="multi-select-dropdown" style={{ position: 'relative', minWidth: '250px' }}>
                {/* Input field with tags */}
                <div
                  style={{
                    border: '1px solid #007bff',
                    borderRadius: '4px',
                    padding: '8px',
                    minHeight: '38px',
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: '4px',
                    backgroundColor: 'white',
                    cursor: 'text'
                  }}
                  onClick={() => setIsFilterDropdownOpen(true)}
                >
                  {/* Selected filter type tags */}
                  {filterTypes.map((typeValue) => {
                    const option = typeOptions.find(opt => opt.value === typeValue);
                    return (
                      <span
                        key={typeValue}
                        style={{
                          backgroundColor: '#8b1634',
                          color: 'white',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}
                      >
                        {option?.label}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleFilterRemoveType(typeValue);
                          }}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: 'white',
                            cursor: 'pointer',
                            padding: '0',
                            fontSize: '12px',
                            lineHeight: '1'
                          }}
                        >
                          ×
                        </button>
                      </span>
                    );
                  })}

                  {/* Input field for typing */}
                  <input
                    type="text"
                    value={filterSearchText}
                    onChange={(e) => setFilterSearchText(e.target.value)}
                    onFocus={() => setIsFilterDropdownOpen(true)}
                    placeholder={filterTypes.length === 0 ? "Filter by types..." : ""}
                    style={{
                      border: 'none',
                      outline: 'none',
                      flex: 1,
                      minWidth: '100px',
                      fontSize: '14px'
                    }}
                  />

                  {/* Clear all button */}
                  {filterTypes.length > 0 && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleFilterClearAll();
                      }}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#6c757d',
                        cursor: 'pointer',
                        padding: '0',
                        fontSize: '16px',
                        lineHeight: '1'
                      }}
                    >
                      ×
                    </button>
                  )}
                </div>

                {/* Dropdown list */}
                {isFilterDropdownOpen && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      backgroundColor: 'white',
                      border: '1px solid #dee2e6',
                      borderRadius: '4px',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      zIndex: 1000,
                      maxHeight: '200px',
                      overflowY: 'auto'
                    }}
                  >
                    {filteredFilterOptions.length > 0 ? (
                      filteredFilterOptions.map((option) => (
                        <div
                          key={option.value}
                          onClick={() => handleFilterTypeSelect(option)}
                          style={{
                            padding: '8px 12px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            backgroundColor: filterTypes.includes(option.value) ? '#FCE4EC' : 'white',
                            borderBottom: '1px solid #f1f3f4'
                          }}
                          onMouseEnter={(e) => {
                            if (!filterTypes.includes(option.value)) {
                              e.target.style.backgroundColor = '#f8f9fa';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!filterTypes.includes(option.value)) {
                              e.target.style.backgroundColor = 'white';
                            }
                          }}
                        >
                          {option.label}
                        </div>
                      ))
                    ) : (
                      <div style={{ padding: '8px 12px', color: '#6c757d', fontSize: '14px' }}>
                        No options found
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Data Table */}
        <div className="table-section">
          <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    checked={isAllSelected}
                    ref={input => {
                      if (input) input.indeterminate = isIndeterminate;
                    }}
                    onChange={handleSelectAll}
                    style={{ margin: 0 }}
                  />
                </th>
                <th>Type</th>
                <th>Date Received</th>
                <th>Date Scheduled</th>
                <th>Priorities</th>
                <th>TT No.</th>
                <th>Client Refer No</th>
                <th>Locations</th>
                <th>Site Details</th>
                <th>Quotation No</th>
                <th>Value</th>
                <th>Job No</th>
                <th>Station</th>
                <th>Description of Works</th>
                <th>Return Site Details</th>
                <th>Return Additional Notes</th>
                <th>Return Visits</th>
                <th>Cherry Picker</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="19" style={{ textAlign: 'center', padding: '20px' }}>
                    Loading Entry Data...
                  </td>
                </tr>
              ) : currentData.length === 0 ? (
                <tr>
                  <td colSpan="19" style={{ textAlign: 'center', padding: '20px' }}>
                    No Entry Data found
                  </td>
                </tr>
              ) : (
                currentData.map((item) => {
                  const rowStyle = !item.type ? { backgroundColor: '#fce4ec' } : {};
                  return (
                  <tr key={item._id || item.id} style={rowStyle}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedItems.has(item._id || item.id)}
                        onChange={() => handleSelectItem(item._id || item.id)}
                        style={{ margin: 0 }}
                      />
                    </td>
                    <td>
                      {item.type ? (
                        <div style={{ display: 'flex', flexWrap: 'nowrap', gap: '4px', overflow: 'hidden' }}>
                          {item.type.split(',').map((typeValue, index) => {
                            const typeOption = typeOptions.find(opt => opt.value === parseInt(typeValue.trim()));
                            return (
                              <span
                                key={index}
                                style={{
                                  fontSize: '10px',
                                  padding: '2px 4px',
                                  borderRadius: '3px',
                                  backgroundColor: '#8b1634',
                                  color: 'white',
                                  display: 'inline-block',
                                  whiteSpace: 'nowrap',
                                  flexShrink: 0
                                }}
                              >
                                {typeOption ? typeOption.label : typeValue.trim()}
                              </span>
                            );
                          })}
                        </div>
  ) : (
    <span style={{ color: '#8b1634', fontSize: '12px' }}>Not assigned</span>
  )}
                    </td>
                    <td>{item.dateReceived || '-'}</td>
                    <td>
                      {(() => {
                        // If item has been scheduled, show the scheduled date from scheduleTask
                        if (item.scheduleTask) {
                          try {
                            const scheduleData = JSON.parse(item.scheduleTask);
                            return scheduleData.scheduleDate || item.dateScheduled || '-';
                          } catch (e) {
                            return item.dateScheduled || '-';
                          }
                        }
                        // Otherwise show the original dateScheduled
                        return item.dateScheduled || '-';
                      })()}
                    </td>
                    <td>{item.priorities || '-'}</td>
                    <td>{item.taylorTechnologyNumber || '-'}</td>
                    <td>{item.clientReferenceNo || '-'}</td>
                    <td>{item.locations || '-'}</td>
                    <td className="site-details-cell">{item.siteDetails || '-'}</td>
                    <td>{item.quotationNo || '-'}</td>
                    <td>{item.value || '-'}</td>
                    <td>{item.jobNo || '-'}</td>
                    <td>{item.station || '-'}</td>
                    <td className="site-details-cell">{item.descriptionOfWorks || '-'}</td>
                    <td className="additional-notes-cell">{item.returnSiteDetails || '-'}</td>
                    <td className="additional-notes-cell">{item.returnAdditionalNotes || '-'}</td>
                    <td>
                      {item.returnVisits ? (
                        <span style={{ color: 'green', fontWeight: 'bold' }}>Yes</span>
                      ) : (
                        <span style={{ color: 'red', fontWeight: 'bold' }}>No</span>
                      )}
                    </td>
                    <td>
                      {item.cherryPickerRequired ? (
                        <span style={{ color: 'green', fontWeight: 'bold' }}>Yes</span>
                      ) : (
                        <span style={{ color: 'red', fontWeight: 'bold' }}>No</span>
                      )}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <button 
                          className="edit-btn"
                          onClick={() => handleEdit(item)}
                          title="Edit this entry"
                          style={{
                            padding: '6px 12px',
                            background: 'linear-gradient(135deg, #8b1634 0%, #a01d3f 100%)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            fontWeight: '500',
                            boxShadow: '0 2px 4px rgba(139, 22, 52, 0.2)',
                            transition: 'all 0.3s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.background = 'linear-gradient(135deg, #6d1128 0%, #8b1634 100%)';
                            e.target.style.transform = 'translateY(-1px)';
                            e.target.style.boxShadow = '0 4px 8px rgba(139, 22, 52, 0.3)';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.background = 'linear-gradient(135deg, #8b1634 0%, #a01d3f 100%)';
                            e.target.style.transform = 'translateY(0)';
                            e.target.style.boxShadow = '0 2px 4px rgba(139, 22, 52, 0.2)';
                          }}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                          </svg>
                          Edit
                        </button>
                        {item.scheduleTask ? (
                          <button 
                            className="schedule-btn scheduled"
                            disabled
                            title="Already scheduled"
                          >
                            Scheduled
                          </button>
                        ) : (
                          <button 
                            className="schedule-btn"
                            onClick={() => handleSchedule(item)}
                            title="Schedule this item"
                          >
                            Schedule
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                  );
                })
              )}
            </tbody>
          </table>
          </div>

          {/* Pagination */}
          <div className="pagination">
          <button 
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            className="pagination-btn"
          >
            Previous
          </button>
          <span className="pagination-info">
            Page {currentPage} of {totalPages}
          </span>
          <button 
            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
            className="pagination-btn"
          >
            Next
          </button>
          </div>
        </div>
      </div>

      {/* Schedule Modal */}
      {showScheduleModal && (
        <div className="modal-overlay" onClick={handleCloseScheduleModal}>
          <div className="modal-content" style={{ maxHeight: 'none', overflowY: 'visible' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Schedule Task</h2>
              <button className="modal-close" onClick={handleCloseScheduleModal}>×</button>
            </div>
            
            <form onSubmit={handleScheduleSubmit} className="modal-form">
              {/* Row 1: Schedule Date and Time */}
              <div className="row">
                <div className="col-md-6">
                  <div className="form-group">
                    <label htmlFor="scheduleDate">Schedule Date *</label>
                    <DatePicker
                      selected={scheduleForm.scheduleDate ? new Date(scheduleForm.scheduleDate) : null}
                      onChange={(date) => {
                        if (date) {
                          const year = date.getFullYear();
                          const month = String(date.getMonth() + 1).padStart(2, '0');
                          const day = String(date.getDate()).padStart(2, '0');
                          const dateValue = `${year}-${month}-${day}`;
                          handleScheduleInputChange({ target: { name: 'scheduleDate', value: dateValue } });
                        } else {
                          handleScheduleInputChange({ target: { name: 'scheduleDate', value: '' } });
                        }
                      }}
                      minDate={new Date()}
                      dateFormat="dd/MM/yyyy"
                      placeholderText="Select Schedule Date"
                      required
                      disabled={isScheduleDateDisabled}
                      className="form-input schedule-date-picker"
                      wrapperClassName="schedule-date-wrapper"
                      calendarClassName="schedule-date-calendar"
                      style={{
                        backgroundColor: isScheduleDateDisabled ? '#e9ecef' : 'white',
                        cursor: isScheduleDateDisabled ? 'not-allowed' : 'pointer',
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-group">
                    <label htmlFor="time">Time *</label>
                    <select
                      id="time"
                      name="time"
                      value={scheduleForm.time}
                      onChange={handleScheduleInputChange}
                      required
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        fontSize: '16px',
                        backgroundColor: 'white'
                      }}
                    >
                      <option value="">Select Time</option>
                      {Array.from({ length: 16 }, (_, i) => {
                        const hour = (i + 8).toString().padStart(2, '0');
                        const timeValue = `${hour}:00`;
                        return (
                          <option key={timeValue} value={timeValue}>
                            {hour}:00
                          </option>
                        );
                      })}
                    </select>
                  </div>
                </div>
              </div>

              {/* Row 2: Estimated Time and Team Selection */}
              <div className="row">
                <div className="col-md-6">
                  <div className="form-group">
                    <label htmlFor="assignmentTime">Estimated Time(Our Hours) *</label>
                    <div className="multi-select-dropdown" style={{ position: 'relative' }}>
                      <div 
                        className="multi-select-input"
                        onClick={() => setIsAssignmentTimeOpen(!isAssignmentTimeOpen)}
                        style={{
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          padding: '10px 12px',
                          cursor: 'pointer',
                          minHeight: '40px',
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: '4px',
                          alignItems: 'center'
                        }}
                      >
                        {scheduleForm.assignmentTime ? (
                          <span style={{ color: '#333' }}>{scheduleForm.assignmentTime} hour</span>
                        ) : (
                          <span style={{ color: '#999' }}>Select assignment time</span>
                        )}
                      </div>
                      
                      {isAssignmentTimeOpen && (
                        <div
                          style={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            right: 0,
                            backgroundColor: 'white',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                            zIndex: 1000,
                            maxHeight: '200px',
                            overflowY: 'auto'
                          }}
                        >
                          {[1, 2, 3].map((hour) => (
                            <div
                              key={hour}
                              onClick={() => handleAssignmentTimeSelect(hour)}
                              style={{
                                padding: '10px 12px',
                                cursor: 'pointer',
                                backgroundColor: scheduleForm.assignmentTime === hour ? '#f0f0f0' : 'white',
                                borderBottom: '1px solid #eee'
                              }}
                            >
                              {hour} hour
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-group">
                    <label htmlFor="teamSelection">Team Selection *</label>
                    <select
                      id="teamSelection"
                      name="teamSelection"
                      value={scheduleForm.teamSelection}
                      onChange={handleScheduleInputChange}
                      required
                    >
                      <option value="">Select team</option>
                      {teams.map((team) => (
                        <option key={team._id} value={team.teamName}>
                          {team.teamName}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className="modal-actions">
                <button 
                  type="button" 
                  onClick={handleCloseScheduleModal}
                  className="btn-cancel"
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  disabled={scheduleSubmitting}
                  className="btn-submit"
                >
                  {scheduleSubmitting ? 'Saving...' : 'Create Schedule'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <div className="modal-overlay" onClick={handleCloseEditModal} style={{ 
          position: 'fixed', 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0, 
          backgroundColor: 'rgba(0, 0, 0, 0.5)', 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          zIndex: 1000,
          padding: '10px'
        }}>
          <div id="editModal" className="modal-content southeastern-edit-modal" style={{ 
            maxWidth: '1200px', 
            width: '100%',
            maxHeight: '95vh',
            background: 'white',
            borderRadius: '12px',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)',
            position: 'relative',
            display: 'flex',
            flexDirection: 'column'
          }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header" style={{
              padding: '12px 16px',
              borderBottom: '2px solid #e9ecef',
              background: 'linear-gradient(135deg, #8b1634 0%, #a01d3f 100%)',
              borderTopLeftRadius: '12px',
              borderTopRightRadius: '12px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              position: 'sticky',
              top: 0,
              zIndex: 10,
              flexShrink: 0
            }}>
              <h2 style={{ 
                margin: 0, 
                fontSize: '1.25rem', 
                fontWeight: '600', 
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                </svg>
                Edit Southeastern Entry
              </h2>
              <button className="modal-close" onClick={handleCloseEditModal} style={{
                background: 'rgba(255, 255, 255, 0.2)',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer',
                color: 'white',
                padding: '4px 10px',
                borderRadius: '6px',
                transition: 'all 0.2s ease',
                lineHeight: '1'
              }}>×</button>
            </div>
            
            <form onSubmit={handleEditSave} className="southeastern-form-content" style={{ 
              padding: '16px',
              overflowY: 'auto',
              flex: 1,
              minHeight: 0
            }}>
              {/* Section 1 - Basic Information */}
              <div className="form-section" style={{
                marginBottom: '16px',
                padding: '12px',
                background: '#f8f9fa',
                borderRadius: '8px',
                border: '1px solid #e9ecef'
              }}>
                <h3 className="section-title" style={{
                  fontSize: '1rem',
                  fontWeight: '600',
                  color: '#8b1634',
                  marginBottom: '12px',
                  paddingBottom: '8px',
                  borderBottom: '2px solid #8b1634'
                }}>Basic Information</h3>
                <div className="form-row" style={{ gap: '12px', marginBottom: '12px' }}>
                  <div className="form-field" style={{ marginBottom: '0' }}>
                    <label className="field-label" style={{ fontSize: '0.875rem', marginBottom: '4px', fontWeight: '500' }}>Date Received *</label>
                    <input
                      type="date"
                      name="dateReceived"
                      value={editForm.dateReceived}
                      onChange={handleEditChange}
                      className={`form-input ${editFieldErrors.dateReceived ? 'error' : ''}`}
                      style={{ padding: '8px 12px', fontSize: '0.875rem' }}
                      required
                    />
                    {editFieldErrors.dateReceived && (
                      <div className="field-error" style={{ fontSize: '0.75rem', marginTop: '4px' }}>{editFieldErrors.dateReceived}</div>
                    )}
                  </div>
                  
                  <div className="form-field" style={{ marginBottom: '0' }}>
                    <label className="field-label" style={{ fontSize: '0.875rem', marginBottom: '4px', fontWeight: '500' }}>Date Scheduled</label>
                    <input
                      type="date"
                      name="dateScheduled"
                      value={editForm.dateScheduled}
                      onChange={handleEditChange}
                      className="form-input"
                      min={new Date().toISOString().split('T')[0]}
                      disabled
                      style={{ 
                        padding: '8px 12px', 
                        fontSize: '0.875rem',
                        backgroundColor: '#e9ecef',
                        cursor: 'not-allowed'
                      }}
                    />
                  </div>
                  
                  <div className="form-field" style={{ marginBottom: '0' }}>
                    <label className="field-label" style={{ fontSize: '0.875rem', marginBottom: '4px', fontWeight: '500' }}>Priorities *</label>
                    <select
                      name="priorities"
                      value={editForm.priorities}
                      onChange={handleEditChange}
                      className={`form-input ${editFieldErrors.priorities ? 'error' : ''}`}
                      style={{ padding: '8px 12px', fontSize: '0.875rem' }}
                      required
                    >
                      <option value="">Select Priority</option>
                      <option value="Priority A (2 days)">Priority A (2 days)</option>
                      <option value="Priority B (3 days)">Priority B (3 days)</option>
                      <option value="Priority C (9 days)">Priority C (9 days)</option>
                    </select>
                    {editFieldErrors.priorities && (
                      <div className="field-error" style={{ fontSize: '0.75rem', marginTop: '4px' }}>{editFieldErrors.priorities}</div>
                    )}
                  </div>
                </div>
              </div>

              {/* Section 2 - Job Details */}
              <div className="form-section" style={{
                marginBottom: '16px',
                padding: '12px',
                background: '#f8f9fa',
                borderRadius: '8px',
                border: '1px solid #e9ecef'
              }}>
                <h3 className="section-title" style={{
                  fontSize: '1rem',
                  fontWeight: '600',
                  color: '#8b1634',
                  marginBottom: '12px',
                  paddingBottom: '8px',
                  borderBottom: '2px solid #8b1634'
                }}>Job Details</h3>
                <div className="form-row" style={{ gap: '12px', marginBottom: '12px' }}>
                  <div className="form-field" style={{ marginBottom: '0' }}>
                    <label className="field-label" style={{ fontSize: '0.875rem', marginBottom: '4px', fontWeight: '500' }}>Taylor Technology Number *</label>
                    <div style={{ position: 'relative' }}>
                      <span style={{ 
                        position: 'absolute', 
                        left: '10px', 
                        top: '50%', 
                        transform: 'translateY(-50%)', 
                        color: '#666',
                        fontWeight: '600',
                        fontSize: '0.875rem'
                      }}>TTL</span>
                      <input
                        type="text"
                        name="taylorTechnologyNumber"
                        value={editForm.taylorTechnologyNumber}
                        onChange={handleEditChange}
                        className={`form-input ${editFieldErrors.taylorTechnologyNumber ? 'error' : ''}`}
                        placeholder="Enter Taylor Technology Number"
                        style={{ padding: '8px 12px 8px 45px', fontSize: '0.875rem' }}
                        inputMode="numeric"
                        required
                      />
                    </div>
                    {editFieldErrors.taylorTechnologyNumber && (
                      <div className="field-error" style={{ fontSize: '0.75rem', marginTop: '4px' }}>{editFieldErrors.taylorTechnologyNumber}</div>
                    )}
                  </div>
                  
                  <div className="form-field" style={{ marginBottom: '0' }}>
                    <label className="field-label" style={{ fontSize: '0.875rem', marginBottom: '4px', fontWeight: '500' }}>Client Reference No *</label>
                    <input
                      type="text"
                      name="clientReferenceNo"
                      value={editForm.clientReferenceNo}
                      onChange={handleEditChange}
                      className={`form-input ${editFieldErrors.clientReferenceNo ? 'error' : ''}`}
                      placeholder="Enter Client Reference Number"
                      style={{ padding: '8px 12px', fontSize: '0.875rem' }}
                      required
                    />
                    {editFieldErrors.clientReferenceNo && (
                      <div className="field-error" style={{ fontSize: '0.75rem', marginTop: '4px' }}>{editFieldErrors.clientReferenceNo}</div>
                    )}
                  </div>
                  
                  <div className="form-field" style={{ marginBottom: '0' }}>
                    <label className="field-label" style={{ fontSize: '0.875rem', marginBottom: '4px', fontWeight: '500' }}>Job No</label>
                    <input
                      type="text"
                      name="jobNo"
                      value={editForm.jobNo}
                      onChange={handleEditChange}
                      className="form-input"
                      placeholder="Enter Job Number"
                      inputMode="numeric"
                      style={{ padding: '8px 12px', fontSize: '0.875rem' }}
                    />
                  </div>
                </div>
                
                <div className="form-row" style={{ gap: '12px' }}>
                  <div className="form-field" style={{ marginBottom: '0' }}>
                    <label className="field-label" style={{ fontSize: '0.875rem', marginBottom: '4px', fontWeight: '500' }}>Quotation No</label>
                    <input
                      type="text"
                      name="quotationNo"
                      value={editForm.quotationNo}
                      onChange={handleEditChange}
                      className="form-input"
                      placeholder="Enter Quotation Number"
                      style={{ padding: '8px 12px', fontSize: '0.875rem' }}
                    />
                  </div>
                  
                  <div className="form-field" style={{ marginBottom: '0' }}>
                    <label className="field-label" style={{ fontSize: '0.875rem', marginBottom: '4px', fontWeight: '500' }}>Value</label>
                    <input
                      type="text"
                      name="value"
                      value={editForm.value}
                      onChange={handleEditChange}
                      className="form-input"
                      placeholder="Enter Value"
                      style={{ padding: '8px 12px', fontSize: '0.875rem' }}
                    />
                  </div>
                  
                  <div className="form-field" style={{ marginBottom: '0' }}>
                    <label className="field-label" style={{ fontSize: '0.875rem', marginBottom: '4px', fontWeight: '500' }}>Station</label>
                    <input
                      type="text"
                      name="station"
                      value={editForm.station}
                      onChange={handleEditChange}
                      className="form-input"
                      placeholder="Enter Station"
                      style={{ padding: '8px 12px', fontSize: '0.875rem' }}
                    />
                  </div>
                </div>
              </div>

              {/* Section 3 - Location & Site Details */}
              <div className="form-section" style={{
                marginBottom: '16px',
                padding: '12px',
                background: '#f8f9fa',
                borderRadius: '8px',
                border: '1px solid #e9ecef'
              }}>
                <h3 className="section-title" style={{
                  fontSize: '1rem',
                  fontWeight: '600',
                  color: '#8b1634',
                  marginBottom: '12px',
                  paddingBottom: '8px',
                  borderBottom: '2px solid #8b1634'
                }}>Location & Site Details</h3>
                <div className="form-row" style={{ marginBottom: '12px' }}>
                  <div className="form-field full-width" style={{ marginBottom: '0' }}>
                    <label className="field-label" style={{ fontSize: '0.875rem', marginBottom: '4px', fontWeight: '500' }}>Locations *</label>
                    <input
                      type="text"
                      name="locations"
                      value={editForm.locations}
                      onChange={handleEditChange}
                      className={`form-input ${editFieldErrors.locations ? 'error' : ''}`}
                      placeholder="Enter Locations"
                      style={{ padding: '8px 12px', fontSize: '0.875rem' }}
                      required
                    />
                    {editFieldErrors.locations && (
                      <div className="field-error" style={{ fontSize: '0.75rem', marginTop: '4px' }}>{editFieldErrors.locations}</div>
                    )}
                  </div>
                </div>
                
                <div className="form-row" style={{ marginBottom: '12px' }}>
                  <div className="form-field full-width" style={{ marginBottom: '0' }}>
                    <label className="field-label" style={{ fontSize: '0.875rem', marginBottom: '4px', fontWeight: '500' }}>Site Details *</label>
                    <textarea
                      name="siteDetails"
                      value={editForm.siteDetails}
                      onChange={handleEditChange}
                      className={`form-textarea ${editFieldErrors.siteDetails ? 'error' : ''}`}
                      placeholder="Enter Site Details"
                      rows="4"
                      style={{ padding: '8px 12px', fontSize: '0.875rem', minHeight: '100px' }}
                      required
                    />
                    {editFieldErrors.siteDetails && (
                      <div className="field-error" style={{ fontSize: '0.75rem', marginTop: '4px' }}>{editFieldErrors.siteDetails}</div>
                    )}
                  </div>
                </div>
                
                <div className="form-row">
                  <div className="form-field full-width" style={{ marginBottom: '0' }}>
                    <label className="field-label" style={{ fontSize: '0.875rem', marginBottom: '4px', fontWeight: '500' }}>Description of Works</label>
                    <textarea
                      name="descriptionOfWorks"
                      value={editForm.descriptionOfWorks}
                      onChange={handleEditChange}
                      className="form-textarea"
                      placeholder="Enter Description of Works"
                      rows="4"
                      style={{ padding: '8px 12px', fontSize: '0.875rem', minHeight: '100px' }}
                    />
                  </div>
                </div>
              </div>

              {/* Section 5 - Requirements */}
              <div className="form-section" style={{
                marginBottom: '16px',
                padding: '12px',
                background: '#f8f9fa',
                borderRadius: '8px',
                border: '1px solid #e9ecef'
              }}>
                <h3 className="section-title" style={{
                  fontSize: '1rem',
                  fontWeight: '600',
                  color: '#8b1634',
                  marginBottom: '12px',
                  paddingBottom: '8px',
                  borderBottom: '2px solid #8b1634'
                }}>Requirements</h3>
                <div className="checkbox-section">
                  <div className="checkbox-field">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="returnVisits"
                        checked={editForm.returnVisits}
                        onChange={handleEditChange}
                        className="checkbox-input"
                      />
                      <span className="checkbox-text">RETURN VISITS</span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Section 6 - Return Details (Conditional) */}
              {editForm.returnVisits && (
                <div className="form-section" style={{
                  marginBottom: '16px',
                  padding: '12px',
                  background: '#f8f9fa',
                  borderRadius: '8px',
                  border: '1px solid #e9ecef'
                }}>
                  <h3 className="section-title" style={{
                    fontSize: '1rem',
                    fontWeight: '600',
                    color: '#8b1634',
                    marginBottom: '12px',
                    paddingBottom: '8px',
                    borderBottom: '2px solid #8b1634'
                  }}>Return Details</h3>
                  
                  <div className="form-row" style={{ marginBottom: '12px' }}>
                    <div className="form-field full-width" style={{ marginBottom: '0' }}>
                      <label className="field-label" style={{ fontSize: '0.875rem', marginBottom: '4px', fontWeight: '500' }}>RETURN Site Details</label>
                      <textarea
                        name="returnSiteDetails"
                        value={editForm.returnSiteDetails}
                        onChange={handleEditChange}
                        className="form-textarea"
                        placeholder="Enter RETURN Site Details"
                        rows="3"
                        style={{ padding: '8px 12px', fontSize: '0.875rem', minHeight: '80px' }}
                      />
                    </div>
                  </div>
                  
                  <div className="form-row" style={{ marginBottom: '12px' }}>
                    <div className="form-field full-width" style={{ marginBottom: '0' }}>
                      <label className="field-label" style={{ fontSize: '0.875rem', marginBottom: '4px', fontWeight: '500' }}>RETURN Additional Notes</label>
                      <textarea
                        name="returnAdditionalNotes"
                        value={editForm.returnAdditionalNotes}
                        onChange={handleEditChange}
                        className="form-textarea"
                        placeholder="Enter RETURN Additional Notes"
                        rows="3"
                        style={{ padding: '8px 12px', fontSize: '0.875rem', minHeight: '80px' }}
                      />
                    </div>
                  </div>
                  
                  {/* Cherry Picker Required Checkbox */}
                  <div className="checkbox-section">
                    <div className="checkbox-field">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          name="cherryPickerRequired"
                          checked={editForm.cherryPickerRequired}
                          onChange={handleEditChange}
                          className="checkbox-input"
                        />
                        <span className="checkbox-text">Cherry Picker Required</span>
                      </label>
                    </div>
                  </div>
                </div>
              )}

              {/* Modal Actions */}
              <div className="modal-actions" style={{ 
                marginTop: '12px', 
                paddingTop: '12px',
                borderTop: '2px solid #e9ecef',
                display: 'flex', 
                justifyContent: 'flex-end', 
                gap: '10px' 
              }}>
                <button 
                  type="button" 
                  onClick={handleCloseEditModal}
                  style={{
                    padding: '8px 20px',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    borderRadius: '6px',
                    border: '1px solid #6c757d',
                    background: 'white',
                    color: '#6c757d',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = '#6c757d';
                    e.target.style.color = 'white';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = 'white';
                    e.target.style.color = '#6c757d';
                  }}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  disabled={editSubmitting}
                  style={{
                    padding: '8px 24px',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    borderRadius: '6px',
                    border: 'none',
                    background: editSubmitting 
                      ? '#6c757d' 
                      : 'linear-gradient(135deg, #28a745 0%, #20c997 100%)',
                    color: 'white',
                    cursor: editSubmitting ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s ease',
                    boxShadow: '0 2px 4px rgba(40, 167, 69, 0.2)'
                  }}
                  onMouseEnter={(e) => {
                    if (!editSubmitting) {
                      e.target.style.transform = 'translateY(-1px)';
                      e.target.style.boxShadow = '0 4px 8px rgba(40, 167, 69, 0.3)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!editSubmitting) {
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = '0 2px 4px rgba(40, 167, 69, 0.2)';
                    }
                  }}
                >
                  {editSubmitting ? 'Saving...' : 'Update'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default EntryDataPage;
